import React, { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Typography from '@mui/joy/Typography';
import CircularProgress from '@mui/joy/CircularProgress';
import {
    Add,
    Search,
    FiberManualRecord,
    TuneRounded,
    PersonOutline,
} from '@mui/icons-material';
import { TicketItem, TicketFilters } from './SupportTickets';

interface TicketListPaneProps {
    tickets: TicketItem[];
    selectedTicket: TicketItem | null;
    onSelect: (ticket: TicketItem) => void;
    filters: TicketFilters;
    onFiltersChange: (filters: TicketFilters) => void;
    hasMore: boolean;
    isLoading: boolean;
    onLoadMore: () => void;
    onCreateNew: () => void;
    isSuperAdmin: boolean;
}

const STATUS_COLORS: Record<string, 'primary' | 'warning' | 'success' | 'neutral' | 'danger'> = {
    'Open': 'primary',
    'In Progress': 'warning',
    'Awaiting Reply': 'neutral',
    'Resolved': 'success',
    'Closed': 'neutral',
};

const PRIORITY_COLORS: Record<string, 'primary' | 'warning' | 'danger' | 'neutral'> = {
    'Low': 'neutral',
    'Medium': 'primary',
    'High': 'warning',
    'Urgent': 'danger',
};

function formatTimeAgo(dateStr: string | null): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function TicketListPane({
    tickets,
    selectedTicket,
    onSelect,
    filters,
    onFiltersChange,
    hasMore,
    isLoading,
    onLoadMore,
    onCreateNew,
    isSuperAdmin,
}: TicketListPaneProps) {
    const [searchInput, setSearchInput] = useState(filters.search);
    const [showFilters, setShowFilters] = useState(false);
    const searchTimer = useRef<NodeJS.Timeout | null>(null);
    const observer = useRef<IntersectionObserver>();

    useEffect(() => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            if (searchInput !== filters.search) {
                onFiltersChange({ ...filters, search: searchInput });
            }
        }, 500);
        return () => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
        };
    }, [searchInput]);

    const lastTicketRef = useCallback(
        (node: HTMLElement | null) => {
            if (isLoading) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    onLoadMore();
                }
            });
            if (node) observer.current.observe(node);
        },
        [isLoading, hasMore, onLoadMore]
    );

    const activeFilterCount = [
        filters.status !== 'All' ? 1 : 0,
        filters.category !== 'All' ? 1 : 0,
        filters.priority !== 'All' ? 1 : 0,
        filters.assigned_to ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    return (
        <>
            {/* Header */}
            <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography level="title-lg" sx={{ fontWeight: 700 }}>
                        Tickets
                    </Typography>
                    <Button
                        size="sm"
                        startDecorator={<Add />}
                        onClick={onCreateNew}
                    >
                        New Ticket
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Input
                        size="sm"
                        placeholder="Search..."
                        startDecorator={<Search sx={{ fontSize: 18 }} />}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <IconButton
                        size="sm"
                        variant={showFilters || activeFilterCount > 0 ? 'soft' : 'outlined'}
                        color={activeFilterCount > 0 ? 'primary' : 'neutral'}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <TuneRounded sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {showFilters && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                        <Select
                            size="sm"
                            value={filters.status}
                            onChange={(_, val) => val && onFiltersChange({ ...filters, status: val })}
                            sx={{ flex: 1, minWidth: 110 }}
                        >
                            <Option value="All">All Status</Option>
                            <Option value="Open">Open</Option>
                            <Option value="In Progress">In Progress</Option>
                            <Option value="Awaiting Reply">Awaiting Reply</Option>
                            <Option value="Resolved">Resolved</Option>
                            <Option value="Closed">Closed</Option>
                        </Select>
                        <Select
                            size="sm"
                            value={filters.category}
                            onChange={(_, val) => val && onFiltersChange({ ...filters, category: val })}
                            sx={{ flex: 1, minWidth: 110 }}
                        >
                            <Option value="All">All Categories</Option>
                            <Option value="General">General</Option>
                            <Option value="Billing">Billing</Option>
                            <Option value="Technical">Technical</Option>
                            <Option value="Leads & Marketing">Leads & Marketing</Option>
                            <Option value="Bookings">Bookings</Option>
                            <Option value="Account">Account</Option>
                            <Option value="Other">Other</Option>
                        </Select>
                        <Select
                            size="sm"
                            value={filters.priority}
                            onChange={(_, val) => val && onFiltersChange({ ...filters, priority: val })}
                            sx={{ flex: 1, minWidth: 100 }}
                        >
                            <Option value="All">All Priority</Option>
                            <Option value="Low">Low</Option>
                            <Option value="Medium">Medium</Option>
                            <Option value="High">High</Option>
                            <Option value="Urgent">Urgent</Option>
                        </Select>
                        {isSuperAdmin && (
                            <Select
                                size="sm"
                                value={filters.assigned_to}
                                onChange={(_, val) => onFiltersChange({ ...filters, assigned_to: val || '' })}
                                sx={{ flex: 1, minWidth: 110 }}
                            >
                                <Option value="">All Assigned</Option>
                                <Option value="unassigned">Unassigned</Option>
                            </Select>
                        )}
                    </Box>
                )}
            </Box>

            {/* Ticket list */}
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {isLoading && tickets.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size="md" />
                    </Box>
                ) : tickets.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                            No tickets found.
                        </Typography>
                        <Button
                            size="sm"
                            variant="soft"
                            sx={{ mt: 2 }}
                            onClick={onCreateNew}
                        >
                            Create your first ticket
                        </Button>
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {tickets.map((ticket, idx) => {
                            const isSelected = selectedTicket?.id === ticket.id;
                            const isLast = idx === tickets.length - 1;

                            return (
                                <ListItem
                                    key={ticket.id}
                                    ref={isLast ? lastTicketRef : undefined}
                                    sx={{ p: 0 }}
                                >
                                    <ListItemButton
                                        selected={isSelected}
                                        onClick={() => onSelect(ticket)}
                                        sx={{
                                            px: 2,
                                            py: 1.25,
                                            gap: 0,
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            transition: 'background-color 0.15s',
                                            ...(ticket.unread && !isSelected && {
                                                bgcolor: 'primary.softBg',
                                            }),
                                        }}
                                    >
                                        <ListItemContent>
                                            {/* Top row: ticket number + time */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                    {ticket.unread && (
                                                        <FiberManualRecord sx={{ fontSize: 8, color: 'primary.500', flexShrink: 0 }} />
                                                    )}
                                                    <Typography
                                                        level="body-xs"
                                                        sx={{ color: 'text.tertiary', fontFamily: 'monospace', fontSize: '0.7rem' }}
                                                    >
                                                        {ticket.ticket_number}
                                                    </Typography>
                                                    <Chip
                                                        size="sm"
                                                        variant="soft"
                                                        color={STATUS_COLORS[ticket.status] || 'neutral'}
                                                        sx={{ '--Chip-minHeight': '18px', fontSize: '0.65rem', px: 0.75 }}
                                                    >
                                                        {ticket.status}
                                                    </Chip>
                                                </Box>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary', flexShrink: 0, fontSize: '0.7rem' }}>
                                                    {formatTimeAgo(ticket.last_message_at || ticket.updated_at)}
                                                </Typography>
                                            </Box>

                                            {/* Subject */}
                                            <Typography
                                                level="title-sm"
                                                sx={{
                                                    fontWeight: ticket.unread ? 700 : 600,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    lineHeight: 1.4,
                                                }}
                                            >
                                                {ticket.subject}
                                            </Typography>

                                            {/* Message preview */}
                                            {ticket.last_message_preview && (
                                                <Typography
                                                    level="body-xs"
                                                    sx={{
                                                        color: 'text.tertiary',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        mt: 0.25,
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {ticket.last_message_preview}
                                                </Typography>
                                            )}

                                            {/* Bottom row: meta */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.65rem' }}>
                                                    {ticket.priority} &middot; {ticket.category}
                                                </Typography>
                                                {isSuperAdmin && (
                                                    <>
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.65rem' }}>
                                                            &middot;
                                                        </Typography>
                                                        <Typography level="body-xs" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>
                                                            {ticket.franchise_name}
                                                        </Typography>
                                                    </>
                                                )}
                                                {isSuperAdmin && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 'auto' }}>
                                                        <PersonOutline sx={{ fontSize: 13, color: ticket.assigned_to_name ? 'text.tertiary' : 'warning.500' }} />
                                                        <Typography
                                                            level="body-xs"
                                                            sx={{
                                                                fontSize: '0.65rem',
                                                                color: ticket.assigned_to_name ? 'text.tertiary' : 'warning.500',
                                                                fontWeight: ticket.assigned_to_name ? 400 : 600,
                                                            }}
                                                        >
                                                            {ticket.assigned_to_name || 'Unassigned'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </ListItemContent>
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                        {isLoading && (
                            <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                                <CircularProgress size="sm" />
                            </ListItem>
                        )}
                    </List>
                )}
            </Box>
        </>
    );
}
